import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FlaskConical, Info } from "lucide-react";
import {
  ILabReportInterpretationListResponseData,
  ILabReportInterpretationPendingRequisition,
} from "@/api_framework/api_modals/LabReportInterpretation";
import ModalDoubleAction from "@/app/components/ui/Modals/DoubleAction";
import { useModal } from "@/app/hooks/ui_hooks/useModal";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";

interface IRequisitionSelectionModalData {
  row: ILabReportInterpretationListResponseData;
  requisitions: ILabReportInterpretationPendingRequisition[];
}

interface IRequisitionSelectionModalProps {
  isSubmitting: boolean;
  onConfirm: (row: ILabReportInterpretationListResponseData, requisitionIds: string[]) => void;
}

const RequisitionSelectionModal = ({ isSubmitting, onConfirm }: IRequisitionSelectionModalProps) => {
  const isOpen = useModal("LAB_REPORT_INTERPRETATION_REQUISITIONS_MODAL");
  const { row, requisitions = [] } = useModalState(
    "LAB_REPORT_INTERPRETATION_REQUISITIONS_MODAL",
  ) as IRequisitionSelectionModalData;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) setSelectedIds(requisitions.map((requisition) => requisition.requisition_id));
  }, [isOpen, requisitions]);

  const toggleRequisition = (requisitionId: string) => {
    setSelectedIds((prev) =>
      prev.includes(requisitionId) ? prev.filter((id) => id !== requisitionId) : [...prev, requisitionId],
    );
  };

  const handleConfirm = () => {
    if (isSubmitting) return;
    if (!selectedIds.length) {
      toast.error("Select at least one requisition");
      return;
    }
    onConfirm(row, selectedIds);
  };

  const reportLabel = `${requisitions.length} ${requisitions.length === 1 ? "report" : "reports"}`;
  const isAllSelected = requisitions.length > 0 && selectedIds.length === requisitions.length;
  const toggleAll = () =>
    setSelectedIds(isAllSelected ? [] : requisitions.map((requisition) => requisition.requisition_id));

  return (
    <ModalDoubleAction
      modalId="LAB_REPORT_INTERPRETATION_REQUISITIONS_MODAL"
      title="Review Lab Reports"
      showIcon={false}
      button={{
        primaryButtonText: isSubmitting ? "Continuing..." : "Continue",
        primaryButtonClassname: selectedIds.length
          ? "bg-black text-white hover:bg-gray-900"
          : "bg-gray-200 text-gray-400 cursor-not-allowed pointer-events-none",
        secondaryButtonText: "Cancel",
        primaryCallback: handleConfirm,
      }}
      content={
        <span className="block">
          <span className="flex items-center gap-4 p-4 rounded-xl bg-[#F5F8FF] border border-[#E0E9FF]">
            <span className="shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-white shadow-sm ring-1 ring-[#E9D5FF]">
              <FlaskConical className="w-6 h-6 text-[#7C3AED]" aria-hidden="true" />
            </span>
            <span className="block min-w-0">
              <span className="block text-base font-semibold text-[#1E3A8A]">
                {reportLabel} of {row?.pet?.name || "-"} uploaded. Please review.
              </span>

              <span className="block mt-1 text-xs text-gray-500">View lab reports and EMR inside.</span>
            </span>
          </span>

          <span className="flex items-center justify-between mt-5 mb-2">
            <span className="text-sm font-semibold text-gray-900">
              Reports to interpret{" "}
              <span className="ml-1 text-xs font-medium text-gray-500">
                {selectedIds.length} of {requisitions.length} selected
              </span>
            </span>
            {requisitions.length > 1 && (
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs font-semibold text-[#1E3A8A] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A8A] rounded-sm"
              >
                {isAllSelected ? "Clear" : "Select all"}
              </button>
            )}
          </span>

          <span className="flex flex-col gap-2 max-h-64 overflow-y-auto">
            {requisitions.map((requisition) => (
              <label
                key={requisition.requisition_id}
                className="flex items-center gap-3 px-3 py-3 rounded-xl border border-gray-200 bg-white cursor-pointer transition-colors hover:border-gray-300 hover:bg-gray-50 has-[:checked]:border-[#1E3A8A] has-[:checked]:bg-[#F5F8FF]"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(requisition.requisition_id)}
                  onChange={() => toggleRequisition(requisition.requisition_id)}
                  className="h-4 w-4 rounded border-gray-300 text-[#1E3A8A] focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-[#1E3A8A] focus-visible:ring-offset-2"
                />
                <span className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate">
                  {requisition.test_name}
                </span>
              </label>
            ))}
          </span>

          <span className="flex items-center gap-1.5 mt-3 text-xs text-gray-500">
            <Info className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            Selected reports are interpreted together in one EMR.
          </span>
        </span>
      }
    />
  );
};

export default RequisitionSelectionModal;
